// Integration catalog for the "Neues Mandat" activation tiles — the authoritative model from the
// handoff prototype (Neues Mandat.html `CATS`): ids, categories, brand colours, mono-letter
// fallbacks, and the dependency links between tiles:
//   pay     — marketplace → its payout tile (auto-enabled when the marketplace is on)
//   linked  — a payout tile bound to a marketplace (not directly clickable)
//   suggest — a shop → a suggested payment provider (pre-selected, freely deselectable)
// Logos are rendered as a coloured mono initial (no external CDN dependency); real logos can be
// wired to the asset pipeline later.

import logoShopify from "../../../_assets/img/registration/logo-shopify.svg";
import logoAmazon from "../../../_assets/img/registration/logo-amazon.png";
import logoWooCommerce from "../../../_assets/img/registration/logo-woocommerce.svg";
import logoEbay from "../../../_assets/img/registration/logo-ebay.svg";
import logoTikTok from "../../../_assets/img/registration/logo-tiktok.svg";
import logoKaufland from "../../../_assets/img/registration/logo-kaufland.png";
import logoOtto from "../../../_assets/img/registration/logo-otto.png";
import logoZalando from "../../../_assets/img/registration/logo-zalando.png";
import logoAboutYou from "../../../_assets/img/registration/logo-aboutyou.png";
import logoStripe from "../../../_assets/img/registration/logo-stripe.svg";
import logoChargebee from "../../../_assets/img/registration/logo-chargebee.png";

// Reuse the existing registration-flow brand logos (src/_assets/img/registration). Payment/payout
// tiles map to their brand's logo; integrations without a shipped logo fall back to the mono initial.
const LOGO_BY_ID = {
    shopify: logoShopify,
    shopify_pay: logoShopify,
    shopify_pos: logoShopify,
    amazon: logoAmazon,
    amazon_payout: logoAmazon,
    woocommerce: logoWooCommerce,
    ebay: logoEbay,
    ebay_pay: logoEbay,
    tiktok: logoTikTok,
    tiktok_pay: logoTikTok,
    kaufland: logoKaufland,
    kaufland_pay: logoKaufland,
    otto: logoOtto,
    otto_pay: logoOtto,
    zalando: logoZalando,
    zalando_pay: logoZalando,
    aboutyou: logoAboutYou,
    aboutyou_pay: logoAboutYou,
    stripe_pay: logoStripe,
    stripe_billing: logoStripe,
    chargebee: logoChargebee,
};

export const getLogoById = (id) => LOGO_BY_ID[id] || null;

export const CATS = [
    {
        id: "shops",
        title: "Shops",
        items: [
            { id: "shopify", name: "Shopify", color: "#5E8E3E", mono: "S", suggest: "shopify_pay" },
            { id: "woocommerce", name: "WooCommerce", color: "#7F54B3", mono: "W" },
        ],
    },
    {
        id: "marketplaces",
        title: "Marktplätze",
        items: [
            { id: "amazon", name: "Amazon", color: "#232F3E", mono: "a", pay: "amazon_payout" },
            { id: "otto", name: "Otto", color: "#E4002B", mono: "O", pay: "otto_pay" },
            { id: "ebay", name: "eBay", color: "#E53238", mono: "e", pay: "ebay_pay" },
            { id: "kaufland", name: "Kaufland", color: "#E10915", mono: "K", pay: "kaufland_pay" },
            { id: "aboutyou", name: "AboutYou", color: "#1A1A1A", mono: "AY", pay: "aboutyou_pay" },
            { id: "tiktok", name: "TikTok", color: "#010101", mono: "T", pay: "tiktok_pay" },
            { id: "zalando", name: "Zalando", color: "#FF6900", mono: "Z", pay: "zalando_pay" },
        ],
    },
    {
        id: "booking",
        title: "Abo-Verwaltung",
        items: [
            { id: "chargebee", name: "Chargebee", color: "#FF7846", mono: "C" },
            { id: "stripe_billing", name: "Stripe Billing", color: "#635BFF", mono: "S" },
        ],
    },
    {
        id: "payment",
        title: "Payment",
        items: [
            { id: "paypal", name: "PayPal", color: "#003087", mono: "P" },
            { id: "stripe_pay", name: "Stripe Payments", color: "#635BFF", mono: "S" },
            { id: "adyen", name: "Adyen", color: "#0ABF53", mono: "A" },
            { id: "klarna", name: "Klarna", color: "#FFB3C7", mono: "K", fg: "#17120f" },
            { id: "mollie", name: "Mollie", color: "#000814", mono: "M" },
            { id: "braintree", name: "Braintree", color: "#009CDE", mono: "B" },
            { id: "gocardless", name: "GoCardless", color: "#F45D48", mono: "G" },
            { id: "unzer", name: "Unzer", color: "#EC5B24", mono: "U" },
            { id: "shopify_pay", name: "Shopify Payments", color: "#5E8E3E", mono: "S" },
            { id: "shopify_pos", name: "Shopify POS", color: "#8AB661", mono: "P" },
            { id: "amazon_payout", name: "Amazon Payout", color: "#232F3E", mono: "a", linked: "amazon" },
            { id: "otto_pay", name: "Otto Payments", color: "#E4002B", mono: "O", linked: "otto" },
            { id: "ebay_pay", name: "eBay Payments", color: "#E53238", mono: "e", linked: "ebay" },
            { id: "kaufland_pay", name: "Kaufland Payments", color: "#E10915", mono: "K", linked: "kaufland" },
            { id: "tiktok_pay", name: "TikTok Payments", color: "#010101", mono: "T", linked: "tiktok" },
            { id: "aboutyou_pay", name: "AboutYou Payments", color: "#1A1A1A", mono: "AY", linked: "aboutyou" },
            { id: "zalando_pay", name: "Zalando Payments", color: "#FF6900", mono: "Z", linked: "zalando" },
        ],
    },
];

// Integrations toggled ON when the page loads (per ticket functional requirement).
export const DEFAULT_ACTIVE = ["shopify", "paypal", "shopify_pay"];

// Platform user types offered in the inline "Benutzer" form, in the prototype's order
// (Kunde / Mitarbeiter / Steuerberatung). NOTE (review): the "Mitarbeiter" → AGENCY mapping is an
// assumption — the AGENCY enum label is "Agentur"; confirm the intended PlatformUserType.
export const PLATFORM_USER_TYPES = [
    { value: "CUSTOMER", label: "Kunde" },
    { value: "AGENCY", label: "Mitarbeiter" },
    { value: "TAXCONSULTANT", label: "Steuerberatung" },
];

// Phone country codes for the phone inputs.
export const PHONE_COUNTRY_CODES = [
    { value: "+49", label: "DE +49" },
    { value: "+43", label: "AT +43" },
    { value: "+41", label: "CH +41" },
];

// Countries for the address blocks.
export const ADDRESS_COUNTRIES = [
    { value: "DE", label: "Deutschland" },
    { value: "AT", label: "Österreich" },
    { value: "CH", label: "Schweiz" },
];

// Maps a catalog tile id → the org `allowedApps` string that activates that integration for the
// organization. Payout tiles (linked to a marketplace) have no separate app entry — they are enabled
// implicitly with their marketplace. Providers without an entry simply don't contribute an app.
// NOTE (review): confirm the exact allowedApps vocabulary with the backend before merge.
export const CATS_ID_TO_ALLOWED_APP = {
    shopify: "SHOPIFY",
    woocommerce: "WOO_COMMERCE",
    amazon: "AMAZON",
    otto: "OTTO",
    ebay: "EBAY",
    kaufland: "KAUFLAND",
    aboutyou: "ABOUT_YOU",
    tiktok: "TIKTOK_SHOP",
    zalando: "ZALANDO",
    chargebee: "CHARGEBEE",
    stripe_billing: "STRIPE_BILLING",
    paypal: "PAYPAL",
    stripe_pay: "STRIPE_PAYMENTS",
    adyen: "ADYEN",
    klarna: "KLARNA",
    mollie: "MOLLIE",
    braintree: "BRAINTREE",
    gocardless: "GOCARDLESS",
    unzer: "UNZER",
    shopify_pay: "SHOPIFY_PAYMENTS",
    shopify_pos: "SHOPIFY_POS",
};

const ITEM_BY_ID = {};
CATS.forEach((category) => category.items.forEach((item) => (ITEM_BY_ID[item.id] = item)));

export const getItemById = (id) => ITEM_BY_ID[id];

export const isPaymentCategory = (categoryId) => categoryId === "payment";
